import { useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export interface OrbSignals {
  unreadMessages: number
  unreadNotifications: number
  dropsBalance: number
}

export interface PacerOrbProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  /** When true: renders without fixed positioning (sits in column flow). Default: false. */
  asInline?: boolean
  /**
   * When true: suppresses the built-in stub panel entirely.
   * Use this when the parent renders its own Pacer sheet/drawer (e.g. desktop PacerSheet).
   * The orb itself still renders with all glow/pulse animations.
   */
  hidePanel?: boolean
  /** Live ambient signals from SSE: unread messages, notifications, drops balance */
  signals?: OrbSignals
  /**
   * When true: the orb's decorative infinite gradient/pulse animations render
   * as a single static frame instead of looping. Pass this from the app's
   * reduced-motion preference (OS-level or the a11y widget's own toggle).
   * Do NOT rely on CSS animation-duration overrides for this — Framer Motion
   * drives these via its own JS tween loop, not native CSS animations, so a
   * CSS `transition-duration: 0.01ms` override does not slow them down; it
   * can make an infinite multi-keyframe loop cycle at a very high frequency
   * instead, which reads as a strobe effect. Default: false.
   */
  reducedMotion?: boolean
}

/**
 * Pacer assistant orb — glowing sun orb with wispy animated color wash.
 *
 * Keyboard: Cmd+P (registered in Layout.tsx)
 *
 * asInline=false (default): fixed bottom-right placement
 * asInline=true:            in-flow element — lives inside the right chrome column
 * hidePanel=true:           orb only — parent renders its own PacerSheet or PacerDrawer
 */
export function PacerOrb({ isOpen, onOpen, onClose, asInline = false, hidePanel = false, signals, reducedMotion = false }: PacerOrbProps) {
  const positionClass = asInline
    ? ''
    : 'fixed bottom-6 end-6 z-50'

  const containerRef = useRef<HTMLDivElement>(null)

  // Click outside to close (only when the orb owns its own panel)
  useEffect(() => {
    if (hidePanel) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    if (isOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, onClose, hidePanel])

  return (
    <div ref={containerRef} className={`${positionClass} group relative flex items-center`}>
      {/* Built-in stub panel — frozen in SWIM Design Vault.
          Suppressed when hidePanel=true (parent renders PacerSheet instead). */}
      {!hidePanel && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="absolute top-0 end-full me-3 z-20 w-80"
              initial={{ opacity: 0, x: 8, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 8, scale: 0.96 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              role="dialog"
              aria-label="Pacer assistant"
              aria-modal="false"
            >
              <div className="relative rounded-2xl p-[2px] overflow-hidden shadow-xl">
                <motion.div
                  className="absolute"
                  style={{
                    width: '200%',
                    height: '200%',
                    top: '-50%',
                    left: '-50%',
                    background: 'conic-gradient(from 0deg, transparent 0%, transparent 70%, #fde68a 78%, #f59e0b 86%, #fb923c 92%, #e8dff5 97%, #a5f3fc 99%, transparent 100%)',
                  }}
                  animate={reducedMotion ? { rotate: 0 } : { rotate: 360 }}
                  transition={reducedMotion ? { duration: 0 } : { duration: 3, repeat: Infinity, ease: 'linear' }}
                />
                <div className="relative rounded-[14px] bg-sand-50 dark:bg-slate-900 overflow-hidden">
                  <div className="p-4 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded-full flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 50%, #fb923c 100%)' }}
                      />
                      <span className="font-sans text-sm font-semibold text-slate-800 dark:text-sand-100">
                        Pacer
                      </span>
                    </div>
                    <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                      Your SWIM assistant is coming soon.
                    </p>
                    <div className="flex justify-end">
                      <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 leading-none">
                        ESC
                      </kbd>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      <div className="relative w-10 h-10">
        {/* Badge overlays (visible only when signals defined) */}
        {signals && (
          <>
            {/* Message badge — top-left */}
            {signals.unreadMessages > 0 && (
              <div className="absolute -top-1 -start-1 z-10">
                <div className="w-5 h-5 rounded-full bg-orange-500 dark:bg-orange-400 flex items-center justify-center">
                  <span className="text-white dark:text-slate-900 text-[10px] font-bold">
                    {signals.unreadMessages > 9 ? '9+' : signals.unreadMessages}
                  </span>
                </div>
              </div>
            )}

            {/* Notification badge — top-right */}
            {signals.unreadNotifications > 0 && (
              <div className="absolute -top-1 -end-1 z-10">
                <div className="w-5 h-5 rounded-full bg-ocean-500 dark:bg-ocean-400 flex items-center justify-center">
                  <span className="text-white dark:text-slate-900 text-[10px] font-bold">
                    {signals.unreadNotifications > 9 ? '9+' : signals.unreadNotifications}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        <motion.button
          onClick={isOpen ? onClose : onOpen}
          aria-label="Open Pacer assistant (⌘P)"
          aria-expanded={isOpen}
          className="relative w-10 h-10 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400 overflow-hidden"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          animate={isOpen ? { scale: 1.05 } : { scale: 1 }}
        >
        {/* Glow pulse ring — always on, slow breath. Static (mid-pulse frame,
            no loop) when reducedMotion. */}
        <motion.span
          className="absolute inset-[-6px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.25) 0%, transparent 70%)' }}
          animate={reducedMotion ? { scale: 1.15, opacity: 0.4 } : { scale: [1, 1.35, 1], opacity: [0.6, 0.2, 0.6] }}
          transition={reducedMotion ? { duration: 0 } : { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Active state ring — brighter when open. No repeat at all when
            reducedMotion — just the plain enter/exit fade. */}
        <AnimatePresence>
          {isOpen && (
            <motion.span
              className="absolute inset-[-4px] rounded-full border border-amber-300"
              initial={{ scale: 1, opacity: 0.8 }}
              animate={reducedMotion ? { scale: 1, opacity: 0.5 } : { scale: 1.5, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={reducedMotion ? { duration: 0.15 } : { duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
            />
          )}
        </AnimatePresence>

        {/* Orb surface — wispy animated color wash. Single static gradient
            (no cycling) when reducedMotion. */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)',
          }}
          animate={
            reducedMotion
              ? { background: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)' }
              : {
                  background: [
                    'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)',
                    'linear-gradient(220deg, #fed7aa 0%, #fbbf24 30%, #e8dff5 60%, #a5f3fc 100%)',
                    'linear-gradient(315deg, #fde68a 0%, #fb923c 40%, #f59e0b 70%, #fed7aa 100%)',
                    'linear-gradient(135deg, #fde68a 0%, #f59e0b 35%, #fb923c 65%, #e8dff5 100%)',
                  ],
                }
          }
          transition={reducedMotion ? { duration: 0 } : { duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Inner glow — the sun core */}
        <div
          className="absolute inset-[6px] rounded-full"
          style={{
            background: 'radial-gradient(circle at 40% 35%, rgba(255,255,255,0.65) 0%, rgba(255,220,80,0.4) 50%, transparent 80%)',
          }}
        />
        </motion.button>

      </div>
    </div>
  )
}
